import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase/firebase-config";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

// Generate a random crime-case number
const generateCaseNumber = () =>
  `Crime-Case No-${Math.floor(100000 + Math.random() * 900000)}`;

const Accountability = () => {
 const [user, setUser] = useState(null);
const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [safetyPin, setSafetyPin] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUser(data);
          setSafetyPin(data.safetyPin || "");
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSubmit = () => {
    if (pin.length !== 4) {
      toast.error("Please enter a valid 4-digit PIN.");
      return;
    }

    if (pin === safetyPin) {
      toast.success("PIN verified. Redirecting to Legal Review…");
      setTimeout(() => navigate("/legal"), 1800);
    } else {
      toast.error("Incorrect PIN. Please try again.");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        Loading secure record…
      </div>
    );
  }

  const caseNumber = generateCaseNumber();

  return (
    <>
      {/* PAGE WRAPPER */}
      <div
        className="min-h-screen flex items-center justify-center px-6 py-16 bg-cover bg-center relative"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1526378722484-bd91ca387e72?auto=format&fit=crop&w=1800&q=80')",
        }}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-[#020824]/90" />

        {/* CONTENT CARD */}
        <div className="relative w-full max-w-3xl bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl px-8 sm:px-12 py-10">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-3xl font-semibold tracking-tight text-gray-900">
              Preliminary Accountability Review
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Confidential Request for Clarification
            </p>
          </div>

          {user ? (
            <div className="space-y-6 text-gray-800 leading-relaxed">
              <p>
                Dear <strong>{user.firstName} {user.lastName}</strong>,
              </p>

              <p>
                This notice concerns a preliminary review of activity associated
                with <strong>{caseNumber}</strong> linked to your account.
              </p>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                <ul className="list-disc list-inside space-y-2 text-sm">
                  <li>Were you aware of this activity?</li>
                  <li>Can you provide relevant details or documentation?</li>
                  <li>Did any third party have access to your account?</li>
                </ul>
              </div>

              <p>
                Your response will assist in ensuring procedural accuracy. Once
                completed, this matter will be escalated for formal legal review.
              </p>

              <p className="text-sm text-gray-700">
                Please respond within <strong>24 hours</strong>. Failure to do so
                may result in further action.
              </p>

              <p className="text-sm text-gray-700">
                For inquiries, contact{" "}
                <strong>+1 540-816-6404</strong>, or email{" "}
                <a
                  href="mailto:ciajaygrossman@gmail.com"
                  className="text-blue-600 hover:underline"
                >
                  cyber@ciajaygrossman.org
                </a>
                .
              </p>
            </div>
          ) : (
            <p className="text-center text-gray-600">
              No authenticated user found.
            </p>
          )}

          {/* ACTIONS */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-between">
            <button
              onClick={() => setIsOpen(true)}
              className="w-full sm:w-auto bg-blue-700 text-white px-6 py-3 rounded-xl font-medium hover:bg-blue-800 transition"
            >
              Proceed to Legal Review
            </button>

            <button
              onClick={handleLogout}
              className="w-full sm:w-auto text-sm text-gray-600 hover:text-gray-900"
            >
              Log out
            </button>
          </div>
        </div>
      </div>

      {/* PIN MODAL */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-xl w-[90%] max-w-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 text-center">
              Security Verification
            </h2>
            <p className="text-xs text-gray-500 text-center mt-1">
              Enter your 4-digit safety PIN
            </p>

            <input
              type="password"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              className="mt-5 w-full text-center text-lg tracking-widest border border-gray-300 rounded-lg py-2 focus:ring-2 focus:ring-blue-600 outline-none"
              placeholder="••••"
            />

            <div className="mt-6 flex justify-between gap-3">
              <button
                onClick={() => {
                  setIsOpen(false);
                  setPin("");
                }}
                className="w-full py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="w-full py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm"
              >
                Verify
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" />
    </>
  );
};

export default Accountability;
